import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/app/AppShell";
import { Btn } from "@/components/ui/Btn/Btn";
import { Checkbox } from "@/components/ui/Checkbox/Checkbox";
import { Icon } from "@/components/ui/Icon/Icon";
import { Modal } from "@/components/ui/Modal/Modal";
import { Input } from "@/components/ui/Input/Input";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { TagInput } from "@/components/ui/TagInput/TagInput";
import { Segmented } from "@/components/ui/Segmented/Segmented";
import { Tabs } from "@/components/ui/Tabs/Tabs";
import { PlatformBadge } from "@/components/ui/PlatformBadge/PlatformBadge";
import { StatusPill, type DraftState } from "@/components/ui/StatusPill/StatusPill";
import { MediaThumb } from "@/components/ui/MediaPreview/MediaPreview";
import { VariantCard, ValidatorRow } from "@/components/draft/VariantCard/VariantCard";
import { FilterChip, type ChipTone } from "@/components/queue/FilterChip";
import { useAuth } from "@/lib/AuthContext";
import {
  useAddComment,
  useApproveDraft,
  useBrand,
  useComments,
  useDrafts,
  useEditDraft,
  useRejectDraft,
  useScheduleDraft,
  useSubmitFeedback,
  useWorkspace,
  type EditVariantInput,
  type WorkspaceRole,
} from "@/lib/queries";
import {
  toDraftView,
  fmtDateTime,
  fmtRelative,
  identityFromEmail,
  type DraftView,
} from "@/lib/draftView";
import { can } from "@notamen/core/workspace";
import { reasonsFor } from "@notamen/core/feedback";
import styles from "./ApprovalQueue.module.css";

type StatusFilter = "all" | DraftState;
type PlatformFilter = "all" | "ig" | "fb" | "tt";

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string; tone: ChipTone }> = [
  { key: "all", label: "All", tone: "neutral" },
  { key: "pending", label: "Pending", tone: "pending" },
  { key: "approved", label: "Approved", tone: "approved" },
  { key: "scheduled", label: "Scheduled", tone: "scheduled" },
  { key: "published", label: "Published", tone: "published" },
  { key: "failed", label: "Failed", tone: "failed" },
  { key: "rejected", label: "Rejected", tone: "rejected" },
];

export function ApprovalQueue() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const wsQuery = useWorkspace(ws);
  const brandQuery = useBrand(ws, brand);
  const draftsQuery = useDrafts(ws, brand);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [sortNewest, setSortNewest] = useState(true);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const approve = useApproveDraft(ws, brand);
  const reject = useRejectDraft(ws, brand);
  const schedule = useScheduleDraft(ws, brand);
  const edit = useEditDraft(ws, brand);

  const role: WorkspaceRole | undefined = wsQuery.data?.role;
  const canApprove = role ? can(role, "approve") : false;
  const canPublish = role ? can(role, "publish") : false;
  const canComment = role ? can(role, "comment") : false;
  const canEdit = role ? can(role, "editDraft") : false;
  const recommendedHashtags = brandQuery.data?.brand.recommendedHashtags ?? [];
  const brandUpdatedAt = brandQuery.data?.brand.updatedAt;

  const views = useMemo<DraftView[]>(
    () => (draftsQuery.data?.drafts ?? []).map(toDraftView),
    [draftsQuery.data],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      all: views.length,
      pending: 0,
      approved: 0,
      scheduled: 0,
      published: 0,
      failed: 0,
      rejected: 0,
    };
    for (const v of views) c[v.status] = (c[v.status] ?? 0) + 1;
    return c;
  }, [views]);

  const filtered = useMemo(() => {
    const list = views.filter((v) => {
      if (statusFilter !== "all" && v.status !== statusFilter) return false;
      if (platformFilter !== "all" && v.platform !== platformFilter) return false;
      return true;
    });
    list.sort((a, b) => {
      const cmp = (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
      return sortNewest ? cmp : -cmp;
    });
    return list;
  }, [views, statusFilter, platformFilter, sortNewest]);

  const selected = views.find((v) => v.id === selectedId) ?? filtered[0];

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/queue`} replace />;
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((d) => d.id)));
  };

  // Bulk approve only touches pending drafts that carry NO blocking warning
  // — a block needs an explicit per-draft override, never a bulk shortcut.
  const bulkApprove = async () => {
    const targets = filtered.filter(
      (d) => selectedIds.has(d.id) && d.status === "pending" && !d.hasBlock,
    );
    for (const d of targets) {
      await approve.mutateAsync({ draftId: d.id }).catch(() => undefined);
    }
    setSelectedIds(new Set());
  };

  const allChecked = selectedIds.size > 0 && selectedIds.size === filtered.length;
  const someChecked = selectedIds.size > 0 && selectedIds.size < filtered.length;

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <div className={styles.topbar}>
          <Btn
            href={`/w/${ws}/b/${brand}`}
            variant="ghost"
            size="sm"
            iconLeft={<Icon name="arrow-left" size={13} />}
          >
            Back to brand
          </Btn>
          <div className={styles.titleWrap}>
            <h1 className={styles.title}>Approval queue</h1>
            <span className={styles.brandName}>{brandQuery.data?.brand.name ?? "…"}</span>
          </div>
          <span className={styles.spacer} />
          <Btn
            variant="primary"
            size="sm"
            iconLeft={<Icon name="check" size={13} />}
            disabled={selectedIds.size === 0 || !canApprove}
            loading={approve.isPending}
            onClick={bulkApprove}
          >
            {selectedIds.size > 0 ? `Approve ${selectedIds.size}` : "Bulk approve"}
          </Btn>
        </div>

        {/* Filter chips bar */}
        <div className={styles.filterBar}>
          {STATUS_FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              label={f.label}
              count={counts[f.key] ?? 0}
              tone={f.tone}
              active={statusFilter === f.key}
              onClick={() => setStatusFilter(f.key)}
            />
          ))}
          <span className={styles.divider} />
          <Segmented
            size="sm"
            value={platformFilter}
            onChange={(k) => setPlatformFilter(k as PlatformFilter)}
            items={[
              { key: "all", label: "All" },
              { key: "ig", label: "IG" },
              { key: "fb", label: "FB" },
              { key: "tt", label: "TT" },
            ]}
          />
          <span className={styles.spacer} />
          <Btn
            variant="ghost"
            size="sm"
            iconLeft={<Icon name="arrow-up-down" size={13} />}
            onClick={() => setSortNewest((s) => !s)}
          >
            Sort: {sortNewest ? "Newest" : "Oldest"}
          </Btn>
        </div>

        {/* Split layout */}
        <div className={styles.split}>
          <div className={styles.listPane}>
            <div className={styles.bulkHeader}>
              <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} />
              <span className={styles.bulkLabel}>
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : `${filtered.length} of ${views.length} drafts`}
              </span>
              {selectedIds.size > 0 && (
                <span className={styles.bulkActions}>
                  <Btn variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                    Clear
                  </Btn>
                  <Btn
                    variant="primary"
                    size="sm"
                    iconLeft={<Icon name="check" size={13} />}
                    disabled={!canApprove}
                    loading={approve.isPending}
                    onClick={bulkApprove}
                  >
                    Approve {selectedIds.size}
                  </Btn>
                </span>
              )}
            </div>

            <div className={styles.list}>
              {draftsQuery.isLoading && <div className={styles.muted}>Loading drafts…</div>}
              {draftsQuery.isError && (
                <div className={styles.muted}>Couldn't load drafts. {draftsQuery.error?.message}</div>
              )}
              {!draftsQuery.isLoading && !draftsQuery.isError && filtered.length === 0 && (
                <QueueEmpty hasAny={views.length > 0} ws={ws} brand={brand} />
              )}
              {filtered.map((d) => (
                <ListRow
                  key={d.id}
                  view={d}
                  selected={selected?.id === d.id}
                  checked={selectedIds.has(d.id)}
                  onSelect={() => setSelectedId(d.id)}
                  onCheck={(checked) => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(d.id);
                      else next.delete(d.id);
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          </div>

          <div className={styles.detailPane}>
            {selected ? (
              <QueueDetail
                key={selected.id}
                view={selected}
                ws={ws}
                brand={brand}
                canApprove={canApprove}
                canPublish={canPublish}
                canComment={canComment}
                canEdit={canEdit}
                recommendedHashtags={recommendedHashtags}
                brandUpdatedAt={brandUpdatedAt}
                approve={approve}
                reject={reject}
                schedule={schedule}
                edit={edit}
              />
            ) : (
              <div className={styles.detailEmpty}>Select a draft from the list to view details.</div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function QueueEmpty({
  hasAny,
  ws,
  brand,
}: {
  hasAny: boolean;
  ws: string | undefined;
  brand: string | undefined;
}) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Icon name="check-circle" size={22} />
      </div>
      <div className={styles.emptyTitle}>
        {hasAny ? "Nothing matches that filter." : "No drafts yet."}
      </div>
      <div className={styles.emptyText}>
        {hasAny
          ? "Try clearing a filter, or write a new draft to fill the queue."
          : "Generate drafts in the studio and they'll show up here for approval."}
      </div>
      <Btn
        href={`/w/${ws}/b/${brand}/generate`}
        variant="primary"
        size="sm"
        iconLeft={<Icon name="zap" size={13} />}
      >
        New draft
      </Btn>
    </div>
  );
}

function ListRow({
  view,
  selected,
  checked,
  onSelect,
  onCheck,
}: {
  view: DraftView;
  selected: boolean;
  checked: boolean;
  onSelect: () => void;
  onCheck: (checked: boolean) => void;
}) {
  const firstWarning = view.warnings[0];
  return (
    <div
      className={`${styles.row} ${selected ? styles.rowSelected : ""}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <span className={styles.rowCheck} onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={checked} onChange={(e) => onCheck(e.target.checked)} />
      </span>
      {view.media && (
        <MediaThumb
          platform={view.platform}
          type={view.media.type ?? "image"}
          {...(view.media.aspect ? { aspect: view.media.aspect } : {})}
          {...(view.media.type === "carousel" && view.media.slides
            ? { count: view.media.slides.length }
            : {})}
        />
      )}
      <div className={styles.rowMain}>
        <div className={styles.rowTop}>
          <PlatformBadge platform={view.platform} size={14} />
          <span className={styles.rowKind}>{view.kindLabel}</span>
          <span className={styles.spacer} />
          <StatusPill state={view.status} size="sm" />
        </div>
        {view.hook && <div className={styles.rowHook}>{view.hook}</div>}
        <div className={styles.rowBody}>{view.body}</div>
        <div className={styles.rowMeta}>
          <span className={styles.rowId}>{view.id.slice(-6)}</span>
          <span className={styles.dot}>·</span>
          <span>{fmtDateTime(view.createdAt)}</span>
          {firstWarning && (
            <>
              <span className={styles.dot}>·</span>
              <span
                className={firstWarning.level === "block" ? styles.warnBlock : styles.warnNote}
              >
                <Icon
                  name={firstWarning.level === "block" ? "x-circle" : "alert"}
                  size={11}
                />
                {view.warnings.length} {view.warnings.length === 1 ? "note" : "notes"}
              </span>
            </>
          )}
          {view.scheduledFor && (
            <>
              <span className={styles.dot}>·</span>
              <span className={styles.scheduledTag}>
                <Icon name="calendar" size={11} />
                {fmtDateTime(view.scheduledFor)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type ActionState =
  | { kind: "override"; draftId: string }
  | { kind: "reject"; draftId: string }
  | { kind: "schedule"; draftId: string }
  | { kind: "edit"; draftId: string }
  | null;

function QueueDetail({
  view,
  ws,
  brand,
  canApprove,
  canPublish,
  canComment,
  canEdit,
  recommendedHashtags,
  brandUpdatedAt,
  approve,
  reject,
  schedule,
  edit,
}: {
  view: DraftView;
  ws: string | undefined;
  brand: string | undefined;
  canApprove: boolean;
  canPublish: boolean;
  canComment: boolean;
  canEdit: boolean;
  recommendedHashtags: string[];
  brandUpdatedAt: string | undefined;
  approve: ReturnType<typeof useApproveDraft>;
  reject: ReturnType<typeof useRejectDraft>;
  schedule: ReturnType<typeof useScheduleDraft>;
  edit: ReturnType<typeof useEditDraft>;
}) {
  const [tab, setTab] = useState("detail");
  const commentsQuery = useComments(ws, brand, view.id);
  const commentCount = commentsQuery.data?.comments.length ?? 0;
  const [action, setAction] = useState<ActionState>(null);
  const [reason, setReason] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [scheduledFor, setScheduledFor] = useState(defaultScheduleLocal());
  const [copied, setCopied] = useState(false);
  // Structured "what didn't work" reasons captured alongside a reject — fed to
  // the learning loop. Separate from the free-text team reason above.
  const submitFeedback = useSubmitFeedback(ws, brand);
  const [fbCaption, setFbCaption] = useState<Set<string>>(new Set());
  const [fbImage, setFbImage] = useState<Set<string>>(new Set());

  const closeAction = () => {
    setAction(null);
    setReason("");
    setOverrideReason("");
    setFbCaption(new Set());
    setFbImage(new Set());
  };

  /** Reject is the primary action; the structured feedback is best-effort and
   *  must never block or roll back the reject. */
  const onReject = () => {
    const ctx = { source: "queue" as const, draftId: view.id, platform: view.platformName };
    if (fbCaption.size > 0) {
      submitFeedback.mutate({
        target: "caption",
        sentiment: "dislike",
        reasons: [...fbCaption],
        ...ctx,
      });
    }
    if (fbImage.size > 0) {
      submitFeedback.mutate({
        target: "image",
        sentiment: "dislike",
        reasons: [...fbImage],
        ...ctx,
      });
    }
    reject.mutate(
      { draftId: view.id, ...(reason.trim() ? { reason: reason.trim() } : {}) },
      { onSuccess: closeAction },
    );
  };

  const toggleFb = (set: Set<string>, setter: (s: Set<string>) => void, code: string) => {
    const next = new Set(set);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setter(next);
  };

  const onApprove = () => {
    if (view.hasBlock) setAction({ kind: "override", draftId: view.id });
    else approve.mutate({ draftId: view.id });
  };

  const copyBody = async () => {
    const text = [view.hook, view.body, view.hashtags.map((h) => `#${h}`).join(" ")]
      .filter(Boolean)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  const busy =
    approve.isPending || reject.isPending || schedule.isPending || edit.isPending;
  // Linear flow: a post can only be scheduled once it's approved, so the
  // Schedule button only shows on approved drafts (the API enforces the
  // same rule). Published posts are terminal and never editable.
  const canEditThis = canEdit && view.status !== "published";
  // The brand brain changed after this post was last reviewed (approved or
  // scheduled) — surface a nudge to re-review, since the content may no
  // longer reflect current guidance. Editing sends it back to pending.
  const reviewedAt = view.approvedAt ?? view.scheduledAt;
  const brandChangedSinceReview =
    (view.status === "approved" || view.status === "scheduled") &&
    !!brandUpdatedAt &&
    !!reviewedAt &&
    brandUpdatedAt > reviewedAt;

  return (
    <div className={styles.detail}>
      <div className={styles.detailHead}>
        <PlatformBadge platform={view.platform} size={22} />
        <div className={styles.detailHeadText}>
          <span className={styles.detailKind}>{view.kindLabel}</span>
          <span className={styles.detailId}>{view.id}</span>
        </div>
        <StatusPill state={view.status} />
      </div>

      {brandChangedSinceReview && (
        <div className={styles.staleNotice}>
          <span className={styles.staleNoticeIcon}>
            <Icon name="alert" size={16} />
          </span>
          <span className={styles.staleNoticeText}>
            This brand was updated after this post was last reviewed. Review and
            re-approve so it reflects the current brand guidance.
          </span>
          {canEditThis && (
            <Btn
              variant="secondary"
              size="sm"
              iconLeft={<Icon name="edit" size={13} />}
              disabled={busy}
              onClick={() => setAction({ kind: "edit", draftId: view.id })}
            >
              Edit
            </Btn>
          )}
        </div>
      )}

      <VariantCard
        platform={view.platform}
        kind={view.kindLabel}
        status={view.status}
        {...(view.hook ? { hook: view.hook } : {})}
        body={view.body}
        hashtags={view.hashtags}
        warnings={view.warnings}
        meta={view.provider}
        {...(view.media ? { media: view.media } : {})}
      />

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: "detail", label: "Detail" },
          { key: "comments", label: `Comments (${commentCount})` },
          { key: "validator", label: `Validator (${view.warnings.length})` },
        ]}
      />

      {tab === "detail" && <DetailTab view={view} />}
      {tab === "comments" && (
        <CommentsTab ws={ws} brand={brand} draftId={view.id} canComment={canComment} />
      )}
      {tab === "validator" && <ValidatorTab view={view} />}

      {/* Action bar */}
      <div className={styles.actionBar}>
        <Btn variant="ghost" size="sm" iconLeft={<Icon name="copy" size={13} />} onClick={copyBody}>
          {copied ? "Copied" : "Copy"}
        </Btn>
        <span className={styles.spacer} />
        {canEditThis && (
          <Btn
            variant="ghost"
            size="sm"
            iconLeft={<Icon name="edit" size={13} />}
            disabled={busy}
            onClick={() => setAction({ kind: "edit", draftId: view.id })}
          >
            Edit
          </Btn>
        )}
        {(view.status === "pending" || view.status === "approved") && (
          <>
            {canApprove && (
              <Btn
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setAction({ kind: "reject", draftId: view.id })}
              >
                Reject
              </Btn>
            )}
            {canPublish && view.status === "approved" && (
              <Btn
                variant="primary"
                size="sm"
                iconLeft={<Icon name="calendar" size={13} />}
                disabled={busy}
                onClick={() => setAction({ kind: "schedule", draftId: view.id })}
              >
                Schedule
              </Btn>
            )}
            {view.status === "pending" && canApprove && (
              <Btn
                variant="primary"
                size="sm"
                iconLeft={<Icon name="check" size={13} />}
                loading={approve.isPending}
                onClick={onApprove}
              >
                Approve
              </Btn>
            )}
          </>
        )}
      </div>

      {(approve.isError || reject.isError || schedule.isError || edit.isError) && (
        <p className={styles.actionError}>
          {approve.error?.message ??
            reject.error?.message ??
            schedule.error?.message ??
            edit.error?.message}
        </p>
      )}

      {/* Reject modal */}
      <Modal
        open={action?.kind === "reject"}
        title="Reject draft"
        onClose={closeAction}
        footer={
          <>
            <Btn variant="ghost" size="sm" onClick={closeAction}>
              Cancel
            </Btn>
            <Btn
              variant="destructive"
              size="sm"
              loading={reject.isPending}
              onClick={onReject}
            >
              Reject
            </Btn>
          </>
        }
      >
        <p className={styles.modalText}>
          Rejected drafts are kept for learning. Add an optional reason for the team.
        </p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this off — voice, accuracy, timing?"
          rows={3}
        />
        {canComment && (
          <div className={styles.fbBlock}>
            <p className={styles.fbHead}>What didn’t work? Tap any — we’ll use it to tune future drafts.</p>
            <span className={styles.fbLabel}>Caption</span>
            <div className={styles.fbChips}>
              {reasonsFor("caption", "dislike").map((r) => (
                <button
                  key={r.code}
                  type="button"
                  className={`${styles.fbChip} ${fbCaption.has(r.code) ? styles.fbChipOn : ""}`}
                  onClick={() => toggleFb(fbCaption, setFbCaption, r.code)}
                  aria-pressed={fbCaption.has(r.code)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <span className={styles.fbLabel}>Image</span>
            <div className={styles.fbChips}>
              {reasonsFor("image", "dislike").map((r) => (
                <button
                  key={r.code}
                  type="button"
                  className={`${styles.fbChip} ${fbImage.has(r.code) ? styles.fbChipOn : ""}`}
                  onClick={() => toggleFb(fbImage, setFbImage, r.code)}
                  aria-pressed={fbImage.has(r.code)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Schedule modal */}
      <Modal
        open={action?.kind === "schedule"}
        title="Schedule draft"
        onClose={closeAction}
        footer={
          <>
            <Btn variant="ghost" size="sm" onClick={closeAction}>
              Cancel
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              iconLeft={<Icon name="calendar" size={13} />}
              loading={schedule.isPending}
              onClick={() => {
                const iso = localToIso(scheduledFor);
                if (!iso) return;
                schedule.mutate({ draftId: view.id, scheduledFor: iso }, { onSuccess: closeAction });
              }}
            >
              Schedule
            </Btn>
          </>
        }
      >
        <p className={styles.modalText}>When should this go live?</p>
        <Input
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
        />
      </Modal>

      {/* Block-override modal (the API rejects approval of a blocked draft
          without override:true + a stored reason). */}
      <Modal
        open={action?.kind === "override"}
        title="Override safety warning"
        onClose={closeAction}
        footer={
          <>
            <Btn variant="ghost" size="sm" onClick={closeAction}>
              Cancel
            </Btn>
            <Btn
              variant="destructive"
              size="sm"
              loading={approve.isPending}
              disabled={!overrideReason.trim()}
              onClick={() =>
                approve.mutate(
                  { draftId: view.id, override: true, overrideReason: overrideReason.trim() },
                  { onSuccess: closeAction },
                )
              }
            >
              Approve anyway
            </Btn>
          </>
        }
      >
        <div className={styles.overrideWarn}>
          <Icon name="x-circle" size={15} color="var(--danger)" />
          <span>This draft has a blocking safety warning. Approving it requires a reason, stored on the record.</span>
        </div>
        {view.warnings
          .filter((w) => w.level === "block")
          .map((w, i) => (
            <ValidatorRow key={i} level="block" text={w.text} />
          ))}
        <Textarea
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="Why is it safe to approve this despite the warning?"
          rows={3}
        />
      </Modal>

      {/* Edit modal — edits land back in the pending queue for re-review. */}
      {action?.kind === "edit" && (
        <EditDraftModal
          view={view}
          recommendedHashtags={recommendedHashtags}
          edit={edit}
          onClose={closeAction}
        />
      )}
    </div>
  );
}

interface EditableVariant {
  body: string;
  hook: string;
  hashtags: string[];
  callToAction: string;
  imageBrief: string;
  notes: string;
}

function EditDraftModal({
  view,
  recommendedHashtags,
  edit,
  onClose,
}: {
  view: DraftView;
  recommendedHashtags: string[];
  edit: ReturnType<typeof useEditDraft>;
  onClose: () => void;
}) {
  const [variants, setVariants] = useState<EditableVariant[]>(() =>
    view.record.draft.variants.map((v) => ({
      body: v.body ?? "",
      hook: v.hook ?? "",
      hashtags: v.hashtags ?? [],
      callToAction: v.callToAction ?? "",
      imageBrief: v.imageBrief ?? "",
      notes: v.notes ?? "",
    })),
  );

  const updateVariant = (i: number, patch: Partial<EditableVariant>) =>
    setVariants((prev) => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  const onSave = () => {
    const payload: EditVariantInput[] = variants.map((v) => ({
      body: v.body,
      hashtags: v.hashtags,
      hook: v.hook,
      callToAction: v.callToAction,
      imageBrief: v.imageBrief,
      notes: v.notes,
    }));
    edit.mutate({ draftId: view.id, variants: payload }, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      title="Edit draft"
      onClose={onClose}
      footer={
        <>
          <Btn variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" size="sm" loading={edit.isPending} onClick={onSave}>
            Save &amp; return to pending
          </Btn>
        </>
      }
    >
      <p className={styles.modalText}>
        Editing re-checks the draft against the brand brain and sends it back to
        the pending queue for re-approval.
      </p>
      <div className={styles.editForm}>
        {view.record.draft.variants.map((variant, i) => {
          const v = variants[i];
          if (!v) return null;
          return (
            <div key={i} className={styles.editVariant}>
              <div className={styles.editVariantHead}>
                <PlatformBadge platform={view.platform} size={14} />
                <span>
                  {variant.platform} · {variant.kind}
                </span>
              </div>

              <label className={styles.editField}>
                <span className={styles.editLabel}>Hook</span>
                <Input
                  value={v.hook}
                  onChange={(e) => updateVariant(i, { hook: e.target.value })}
                  placeholder="Scroll-stopping first line"
                />
              </label>

              <label className={styles.editField}>
                <span className={styles.editLabel}>Body</span>
                <Textarea
                  value={v.body}
                  onChange={(e) => updateVariant(i, { body: e.target.value })}
                  rows={5}
                />
              </label>

              <div className={styles.editField}>
                <span className={styles.editLabel}>Hashtags</span>
                <TagInput
                  value={v.hashtags}
                  onChange={(next) => updateVariant(i, { hashtags: next })}
                  placeholder="add a hashtag…"
                />
                {recommendedHashtags.length > 0 && (
                  <div className={styles.recos}>
                    <span className={styles.recosLabel}>Quick add:</span>
                    {recommendedHashtags.map((tag) => {
                      const norm = tag.replace(/^#+/, "");
                      const present = v.hashtags.some(
                        (h) => h.replace(/^#+/, "").toLowerCase() === norm.toLowerCase(),
                      );
                      return (
                        <button
                          type="button"
                          key={tag}
                          className={styles.recoChip}
                          disabled={present}
                          onClick={() =>
                            updateVariant(i, { hashtags: [...v.hashtags, norm] })
                          }
                        >
                          #{norm}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <label className={styles.editField}>
                <span className={styles.editLabel}>Call to action</span>
                <Input
                  value={v.callToAction}
                  onChange={(e) => updateVariant(i, { callToAction: e.target.value })}
                  placeholder="e.g. Shop the link in bio"
                />
              </label>

              <label className={styles.editField}>
                <span className={styles.editLabel}>Image brief</span>
                <Textarea
                  value={v.imageBrief}
                  onChange={(e) => updateVariant(i, { imageBrief: e.target.value })}
                  rows={2}
                />
              </label>

              <label className={styles.editField}>
                <span className={styles.editLabel}>Notes</span>
                <Textarea
                  value={v.notes}
                  onChange={(e) => updateVariant(i, { notes: e.target.value })}
                  rows={2}
                />
              </label>
            </div>
          );
        })}
      </div>
      {edit.isError && <p className={styles.actionError}>{edit.error?.message}</p>}
    </Modal>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailRowLabel}>{label}</span>
      <div className={styles.detailRowValue}>{children}</div>
    </div>
  );
}

function DetailTab({ view }: { view: DraftView }) {
  return (
    <div className={styles.detailBody}>
      <DetailRow label="Platform">
        <span className={styles.tag}>{view.platformName}</span>
      </DetailRow>
      <DetailRow label="Content kind">
        <span className={styles.tag}>{view.kindLabel}</span>
      </DetailRow>
      <DetailRow label="Engine">
        <span className={styles.mono}>{view.provider}</span>
      </DetailRow>
      <DetailRow label="Generated">
        <span>{fmtDateTime(view.createdAt)}</span>
      </DetailRow>
      {view.status === "approved" && view.approvedBy && (
        <DetailRow label="Approved">
          <span>by {view.approvedBy}</span>
        </DetailRow>
      )}
      {view.scheduledFor && (
        <DetailRow label="Scheduled">
          <span className={styles.scheduledTag}>
            <Icon name="calendar" size={13} />
            {fmtDateTime(view.scheduledFor)}
            {view.scheduledBy ? ` · by ${view.scheduledBy}` : ""}
          </span>
        </DetailRow>
      )}
      {view.rejectedReason && (
        <DetailRow label="Rejected">
          <span className={styles.rejectedReason}>“{view.rejectedReason}”</span>
        </DetailRow>
      )}
      {view.overrideReason && (
        <DetailRow label="Override">
          <span className={styles.rejectedReason}>“{view.overrideReason}”</span>
        </DetailRow>
      )}
    </div>
  );
}

function ValidatorTab({ view }: { view: DraftView }) {
  if (view.warnings.length === 0) {
    return (
      <div className={styles.detailBody}>
        <ValidatorRow level="ok" text="All checks passed. Safe to approve." />
      </div>
    );
  }
  return (
    <div className={styles.validatorList}>
      {view.warnings.map((w, i) => (
        <ValidatorRow key={i} level={w.level} text={w.text} />
      ))}
    </div>
  );
}

function CommentsTab({
  ws,
  brand,
  draftId,
  canComment,
}: {
  ws: string | undefined;
  brand: string | undefined;
  draftId: string;
  canComment: boolean;
}) {
  const commentsQuery = useComments(ws, brand, draftId);
  const addComment = useAddComment(ws, brand, draftId);
  const [text, setText] = useState("");

  const comments = commentsQuery.data?.comments ?? [];

  const submit = () => {
    const body = text.trim();
    if (!body) return;
    addComment.mutate({ body }, { onSuccess: () => setText("") });
  };

  return (
    <div className={styles.comments}>
      {commentsQuery.isLoading && <div className={styles.muted}>Loading comments…</div>}
      {!commentsQuery.isLoading && comments.length === 0 && (
        <div className={styles.commentsEmpty}>No comments yet. Start the discussion.</div>
      )}
      {comments.map((c) => {
        const who = identityFromEmail(c.authorEmail);
        return (
          <div key={c.id} className={styles.comment}>
            <span className={styles.commentAvatar} aria-hidden="true">
              {who.initials}
            </span>
            <div className={styles.commentMain}>
              <div className={styles.commentHead}>
                <span className={styles.commentWho}>{who.name}</span>
                <span className={styles.commentRole}>{c.authorRole}</span>
                <span className={styles.commentWhen}>{fmtRelative(c.createdAt)}</span>
              </div>
              <div className={styles.commentBody}>{c.body}</div>
            </div>
          </div>
        );
      })}

      {canComment ? (
        <div className={styles.commentForm}>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment for the team…"
            rows={2}
          />
          {addComment.isError && <p className={styles.actionError}>{addComment.error?.message}</p>}
          <div className={styles.commentFormActions}>
            <Btn
              variant="primary"
              size="sm"
              loading={addComment.isPending}
              disabled={!text.trim()}
              onClick={submit}
            >
              Comment
            </Btn>
          </div>
        </div>
      ) : (
        <p className={styles.muted}>Your role can view comments but can't post.</p>
      )}
    </div>
  );
}

/** Default schedule = now + 1 hour, formatted for <input type=datetime-local>. */
function defaultScheduleLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value (local wall-clock) → ISO 8601, or "" if invalid. */
function localToIso(local: string): string {
  if (!local) return "";
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}
